import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import { KLARNA_ENABLED } from "../config";
import Footer from "../components/Footer";
import CourseCard from "../components/CourseCard";
import IntroVideo from "../components/IntroVideo";
import MediaGallery from "../components/MediaGallery";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { ShieldCheck, Wind, Building2, ArrowRight, Sparkles, Layers, UserRound, Lock, CheckCircle2, MessageSquareText, Instagram, Hand } from "lucide-react";

const INSTAGRAM_URL = "https://instagram.com/airdronejet";

export default function Home() {
  const [data, setData] = useState(null);
  const [o2o, setO2o] = useState(null);
  const [showWelcome, setShowWelcome] = useState(() => !sessionStorage.getItem("adj_welcome_seen"));
  const { user, login } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const closeWelcome = () => {
    sessionStorage.setItem("adj_welcome_seen", "1");
    setShowWelcome(false);
  };

  useEffect(() => {
    api.get(`/courses?lang=${lang}`).then((res) => setData(res.data)).catch(() => {});
    api.get("/one-to-one").then((res) => setO2o(res.data)).catch(() => {});
  }, [user, lang]);

  const checkout = async (lookup_key) => {
    if (!user) { login(); return; }
    try {
      const res = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("payment.checkoutError"));
    }
  };

  const courses = data?.courses || [];

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white">
      {showWelcome && <IntroVideo onClose={closeWelcome} />}
      <Navbar />

      {/* HERO */}
      <section className="relative pt-16 overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-20" style={{ background: "radial-gradient(circle, #F5842B, transparent 70%)" }} />
        <div className="absolute top-40 -left-40 w-[420px] h-[420px] rounded-full blur-3xl opacity-10" style={{ background: "radial-gradient(circle, #2E9BD6, transparent 70%)" }} />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="animate-float-up">
            <span className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-sm text-white/90 mb-5" data-testid="hero-greeting">
              <Hand size={15} className="text-[#F5842B]" /> {t("hero.greeting")}
            </span>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#F5842B]/90 flex items-center gap-2">
              <Wind size={14} /> {t("hero.eyebrow")}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[0.95] mt-6">
              {t("hero.title1")} <span className="adj-gradient-text">{t("hero.title2")}</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed mt-6 max-w-xl">{t("hero.subtitle")}</p>
            <div className="flex flex-wrap items-center gap-4 mt-9">
              <a href="#corsi" className="adj-gradient text-white font-semibold px-7 py-3.5 rounded-full flex items-center gap-2 hover:opacity-90 transition-opacity" data-testid="hero-explore-btn">
                {t("hero.exploreCta")} <ArrowRight size={18} />
              </a>
              <a href="#bundle" className="glass text-white font-semibold px-7 py-3.5 rounded-full hover:border-[#F5842B]/50 transition-colors" data-testid="hero-bundle-btn">
                {t("hero.bundleCta")}
              </a>
            </div>
            <div className="flex items-center gap-8 mt-12">
              {[["5", t("hero.stat1")], ["20+", t("hero.stat2")], ["4", t("hero.stat3")]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-3xl font-extrabold adj-gradient-text">{n}</div>
                  <div className="text-xs text-slate-400 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative animate-float-up" style={{ animationDelay: "150ms" }}>
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60">
              <img src="/hero.png" alt="Drone cleaning" className="w-full h-[460px] object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(11,14,20,0.95))" }} />
              <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-5 flex items-center gap-4">
                <img src="/adj-logo.png" alt="ADJ" className="h-12 w-12 object-contain" />
                <div>
                  <div className="font-display font-bold text-white">ADJ AirDroneJet</div>
                  <div className="text-xs text-slate-300 font-mono tracking-wider">{t("hero.brandTag")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO VIDEO (inline, click-to-play) */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-6 pb-10" data-testid="intro-video-section">
        <div className="text-center mb-6">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#F5842B]/90">{t("videoSec.title")}</span>
          <p className="text-slate-400 text-sm mt-2 max-w-xl mx-auto">{t("videoSec.subtitle")}</p>
        </div>
        <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60 bg-black group">
          <video
            src="/intro.mp4"
            poster="/intro-poster.jpg"
            controls
            playsInline
            preload="metadata"
            className="w-full aspect-video object-contain bg-black"
            data-testid="intro-video"
          />
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-2 gap-6">
        {[
          { icon: ShieldCheck, title: t("values.v1Title"), desc: t("values.v1Desc") },
          { icon: Building2, title: t("values.v2Title"), desc: t("values.v2Desc") },
        ].map((v, i) => (
          <div key={i} className="glass rounded-2xl p-7 hover:border-[#F5842B]/40 transition-colors" data-testid={`value-prop-${i}`}>
            <div className="h-12 w-12 rounded-xl adj-gradient flex items-center justify-center mb-5">
              <v.icon size={22} className="text-white" />
            </div>
            <h3 className="font-display text-xl font-bold text-white">{v.title}</h3>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{v.desc}</p>
          </div>
        ))}
      </section>

      {/* COURSES */}
      <section id="corsi" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 scroll-mt-20">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#F5842B]/90">{t("coursesSec.eyebrow")}</span>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mt-3">{t("coursesSec.title")}</h2>
          </div>
          <p className="text-slate-400 text-sm max-w-sm">{t("coursesSec.desc")}</p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {courses.map((c, i) => <CourseCard key={c.slug} course={c} index={i} />)}
        </div>
      </section>

      {/* BUNDLE */}
      <section id="bundle" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 scroll-mt-20">
        <div className="relative rounded-3xl overflow-hidden border border-[#F5842B]/30 p-8 sm:p-14" style={{ background: "linear-gradient(135deg, rgba(245,132,43,0.12), rgba(226,59,46,0.08))" }}>
          <div className="absolute inset-0 dot-grid opacity-30" />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#F5842B] flex items-center gap-2">
                <Sparkles size={14} /> {t("bundle.eyebrow")}
              </span>
              <h2 className="font-display text-3xl lg:text-4xl font-extrabold mt-4">{t("bundle.title")}</h2>
              <p className="text-slate-300 mt-4 max-w-md leading-relaxed">{t("bundle.desc")}</p>
              <div className="flex items-center gap-4 mt-7">
                <span className="font-display text-5xl font-extrabold adj-gradient-text">€600</span>
                <div className="text-sm">
                  <div className="text-slate-400 line-through">€950</div>
                  <div className="text-emerald-400 font-semibold flex items-center gap-1"><Layers size={14} /> {t("bundle.save")}</div>
                </div>
              </div>
              {KLARNA_ENABLED && (
                <div className="flex items-center gap-2 mt-4" data-testid="bundle-installment">
                  <img src="/klarna.svg" alt="Klarna" className="h-4 w-auto" />
                  <span className="text-sm text-slate-300">{t("card.installment", { amount: "200,00" })}</span>
                </div>
              )}
              <button onClick={() => checkout(data?.bundle_lookup_key)} disabled={data?.owns_bundle} className="mt-8 adj-gradient text-white font-semibold px-8 py-4 rounded-full flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50" data-testid="bundle-buy-btn">
                {data?.owns_bundle ? t("bundle.owned") : t("bundle.buy")} <ArrowRight size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {courses.map((c) => (
                <div key={c.slug} className="glass rounded-xl p-4 flex items-center gap-3">
                  <span className="font-mono text-xs px-2 py-1 rounded" style={{ backgroundColor: `${c.accent}22`, color: c.accent }}>{c.code}</span>
                  <span className="font-display font-semibold text-white">{c.title}</span>
                  <span className="text-xs text-slate-500 ml-auto hidden sm:block">{c.subtitle}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ONE TO ONE */}
      <section id="one-to-one" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 scroll-mt-20">
        <div className="relative rounded-3xl overflow-hidden border border-white/10 grid lg:grid-cols-2" data-testid="one2one-section">
          <div className="relative min-h-[320px]">
            <img src="/one-to-one.jpg" alt="One to One coaching" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, transparent 40%, #0B0E14 100%)" }} />
            <div className="absolute inset-0 lg:hidden" style={{ background: "linear-gradient(180deg, transparent 30%, #0B0E14 95%)" }} />
          </div>
          <div className="relative p-8 sm:p-12 bg-[#121721]">
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#F5842B] flex items-center gap-2">
              <UserRound size={14} /> {t("one2one.eyebrow")}
            </span>
            <h2 className="font-display text-3xl lg:text-4xl font-extrabold mt-4">{t("one2one.title")}</h2>
            <p className="text-slate-300 mt-4 leading-relaxed">{t("one2one.desc")}</p>

            <ul className="mt-6 space-y-3">
              {t("one2one.benefits").map((b, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-200 text-sm">
                  <MessageSquareText size={16} className="text-[#F5842B] shrink-0" /> {b}
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-3 mt-8">
              <span className="font-display text-4xl font-extrabold adj-gradient-text">€150</span>
              <span className="text-sm text-slate-400">{t("one2one.perSession")}</span>
            </div>

            {o2o?.purchased ? (
              <div className="mt-6 flex items-center gap-2 text-emerald-300 font-semibold" data-testid="one2one-purchased">
                <CheckCircle2 size={18} /> {t("one2one.purchased")}
              </div>
            ) : o2o?.eligible ? (
              <button onClick={() => checkout("one_to_one")} className="mt-7 adj-gradient text-white font-semibold px-8 py-4 rounded-full flex items-center gap-2 hover:opacity-90 transition-opacity" data-testid="one2one-buy-btn">
                {t("one2one.buy")} <ArrowRight size={18} />
              </button>
            ) : (
              <div className="mt-7">
                <div className="glass rounded-xl p-4 flex items-start gap-3 border border-white/10" data-testid="one2one-locked">
                  <Lock size={18} className="text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold text-white text-sm">{t("one2one.lockedTitle")}</div>
                    <div className="text-xs text-slate-400 mt-1">{user ? t("one2one.lockedMsg") : t("one2one.loginHint")}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* MEDIA GALLERY */}
      <section id="media" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 scroll-mt-20" data-testid="media-section">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#F5842B]/90 flex items-center gap-2">
              <Instagram size={14} /> {t("gallery.eyebrow")}
            </span>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mt-3">{t("gallery.title")}</h2>
            <p className="text-slate-400 text-sm mt-3 max-w-lg">{t("gallery.desc")}</p>
          </div>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="adj-gradient text-white font-semibold px-6 py-3 rounded-full flex items-center gap-2 hover:opacity-90 transition-opacity shrink-0" data-testid="gallery-instagram-btn">
            <Instagram size={18} /> {t("gallery.cta")}
          </a>
        </div>
        <MediaGallery />
      </section>

      <Footer />
    </div>
  );
}
